import { create } from "zustand";
import { hc } from "@/frontend/lib/hc";

export interface DailyCapQueuedCampaign {
  campaignId: string;
  name: string;
  count: number;
}

export interface DailyCapStatus {
  timezone: string;
  day: string | null;
  cap: number | null;
  warnPercent: number | null;
  webhookId: string | null;
  sentToday: number;
  pausedAt: string | null;
  queued: DailyCapQueuedCampaign[];
  canManage: boolean;
}

interface CapWebhook {
  id: string;
  name: string;
}

interface DailyCapState {
  orgId: string | null;
  status: DailyCapStatus | null;
  webhooks: CapWebhook[];
  // Settings form drafts (strings so inputs can be cleared while editing).
  capDraft: string;
  warnDraft: string;
  webhookDraft: string;
  // Banner resume dialog.
  resumeOpen: boolean;
  resumeCapDraft: string;
  dropOpen: boolean;
  busy: boolean;
  fetchStatus: (orgId: string) => Promise<void>;
  fetchWebhooks: (orgId: string) => Promise<void>;
  setDrafts: (drafts: Partial<Pick<DailyCapState, "capDraft" | "warnDraft" | "webhookDraft" | "resumeCapDraft">>) => void;
  setResumeOpen: (open: boolean) => void;
  setDropOpen: (open: boolean) => void;
  saveSettings: () => Promise<string | null>;
  resume: () => Promise<string | null>;
  drop: () => Promise<{ dropped: number } | { error: string }>;
}

const api = () => hc.api["org-settings"]["waba-daily-cap"];

function parseDraft(value: string): number | null | "invalid" {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : "invalid";
}

export const useDailyCapStore = create<DailyCapState>((set, get) => ({
  orgId: null,
  status: null,
  webhooks: [],
  capDraft: "",
  warnDraft: "",
  webhookDraft: "",
  resumeOpen: false,
  resumeCapDraft: "",
  dropOpen: false,
  busy: false,

  fetchStatus: async (orgId) => {
    try {
      const response = await api().$get();
      if (!response.ok) {
        set({ orgId, status: null });
        return;
      }
      const data = await response.json();
      if (!data.success) return;
      const switched = get().orgId !== orgId;
      set({
        orgId,
        status: data.data,
        // Only seed drafts on first load for this organization so a poll
        // does not overwrite what an admin is typing.
        ...(switched || get().status === null ? {
          capDraft: data.data.cap?.toString() ?? "",
          warnDraft: data.data.warnPercent?.toString() ?? "",
          webhookDraft: data.data.webhookId ?? "",
        } : {}),
      });
    } catch (error) {
      console.error("Failed to load daily send cap:", error);
    }
  },

  fetchWebhooks: async (orgId) => {
    try {
      const response = await hc.api.integrations.$get({ query: { orgId } });
      if (!response.ok) return;
      const data = await response.json();
      set({
        webhooks: data.integrations
          .filter((integration) => integration.type === "webhook" && integration.isConnected)
          .map((integration) => ({ id: integration.id, name: integration.name })),
      });
    } catch (error) {
      console.error("Failed to load webhooks:", error);
    }
  },

  setDrafts: (drafts) => set(drafts),
  setResumeOpen: (open) => set((state) => ({
    resumeOpen: open,
    resumeCapDraft: open ? String((state.status?.cap ?? 0) || "") : state.resumeCapDraft,
  })),
  setDropOpen: (open) => set({ dropOpen: open }),

  saveSettings: async () => {
    const { capDraft, warnDraft, webhookDraft, orgId } = get();
    const cap = parseDraft(capDraft);
    const warnPercent = parseDraft(warnDraft);
    if (cap === "invalid") return "Daily cap must be a whole number above 0.";
    if (warnPercent === "invalid" || (warnPercent !== null && warnPercent > 99)) {
      return "Warning threshold must be a whole number from 1 to 99.";
    }
    set({ busy: true });
    try {
      const response = await api().$put({ json: { cap, warnPercent, webhookId: webhookDraft || null } });
      const data = await response.json();
      if (!data.success) return "error" in data ? String(data.error) : "Could not save the daily cap.";
      if (orgId) await get().fetchStatus(orgId);
      return null;
    } catch {
      return "Could not save the daily cap.";
    } finally {
      set({ busy: false });
    }
  },

  resume: async () => {
    const { resumeCapDraft, status, orgId } = get();
    // Resuming never removes the cap; an empty field is a mistake, not "no cap".
    const cap = parseDraft(resumeCapDraft);
    if (cap === "invalid" || cap === null) return "Enter a daily cap above 0.";
    set({ busy: true });
    try {
      const response = await api().resume.$post({
        json: cap === status?.cap ? {} : { cap },
      });
      const data = await response.json();
      if (!data.success) {
        return "error" in data && data.error === "raise_cap_required"
          ? `Today's ${status?.sentToday ?? 0} sends already reach this cap. Raise it to resume today.`
          : "Could not resume sending.";
      }
      set({ resumeOpen: false });
      if (orgId) await get().fetchStatus(orgId);
      return null;
    } catch {
      return "Could not resume sending.";
    } finally {
      set({ busy: false });
    }
  },

  drop: async () => {
    const { orgId } = get();
    set({ busy: true });
    try {
      const response = await api().drop.$post();
      const data = await response.json();
      if (!data.success || !("data" in data)) return { error: "Could not drop the queued messages." };
      set({ dropOpen: false });
      if (orgId) await get().fetchStatus(orgId);
      return { dropped: data.data.dropped };
    } catch {
      return { error: "Could not drop the queued messages." };
    } finally {
      set({ busy: false });
    }
  },
}));
